import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { track } from '@/lib/analytics';
import {
  FiArrowLeft,
  FiGrid,
  FiBook,
  FiHeart,
  FiShoppingBag,
  FiActivity,
  FiShoppingCart,
  FiTruck,
  FiUsers,
  FiFilm,
  FiMoreHorizontal,
  FiX,
  FiMessageCircle,
  FiCheckCircle,
} from 'react-icons/fi';
import { FaGavel } from 'react-icons/fa';
import { Card, Button } from '@/components';
import { apiJson, ApiError } from '@/lib/api';
import { formatMoney } from '@/lib/utils';
import type {
  Expense,
  ExpenseCategoryListResponse,
  UserResponse,
  HouseholdResponse,
} from '@/lib/types';

// Icon component map
const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  general: FiGrid,
  school: FiBook,
  medical: FiHeart,
  clothing: FiShoppingBag,
  activities: FiActivity,
  groceries: FiShoppingCart,
  transportation: FiTruck,
  childcare: FiUsers,
  entertainment: FiFilm,
  other: FiMoreHorizontal,
};

function ExpenseDetailPage() {
  const { id } = Route.useParams();
  const expenseId = Number(id);
  const { getToken } = useAuth();
  const navigate = useNavigate();

  // Track screen view
  useEffect(() => {
    track('Expense Detail Screen');
  }, []);
  const queryClient = useQueryClient();

  const [disputeModalVisible, setDisputeModalVisible] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Fetch expense
  const expenseQuery = useQuery({
    queryKey: ['expense', expenseId],
    queryFn: () => apiJson<Expense>(getToken, `/v1/expenses/${expenseId}`),
    enabled: Number.isFinite(expenseId) && expenseId > 0,
  });

  // Fetch categories
  const categoriesQuery = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => apiJson<ExpenseCategoryListResponse>(getToken, '/v1/expense-categories'),
  });

  // Fetch user
  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
  });

  // Fetch households
  const householdsQuery = useQuery({
    queryKey: ['households'],
    queryFn: async () => {
      try {
        return await apiJson<HouseholdResponse[]>(getToken, '/v1/households');
      } catch (err) {
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          return [] as HouseholdResponse[];
        }
        throw err;
      }
    },
  });

  const expense = expenseQuery.data;
  const categories = categoriesQuery.data?.categories ?? [];
  const category = categories.find((c) => c.expense_category_id === expense?.category);
  const categoryName = category?.name ?? expense?.category ?? '';
  const categoryColor = category?.color ?? '#018B9C';
  const IconComponent = categoryIcons[expense?.category ?? 'general'] || FiGrid;
  const currentUser = userQuery.data;
  const households = householdsQuery.data ?? [];
  const firstHousehold = households[0];

  const getUserName = (userId: number): string => {
    if (!firstHousehold) return `User ${userId}`;
    const member = firstHousehold.members.find((m) => m.user_id === userId);
    if (!member) return `User ${userId}`;
    if (member.first_name && member.last_name) return `${member.first_name} ${member.last_name}`;
    if (member.first_name) return member.first_name;
    return `User ${userId}`;
  };

  const canDelete = expense && currentUser && expense.creator_user_id === currentUser.id;
  const hasDispute = Boolean(expense?.disputes && expense.disputes.length > 0);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => apiJson(getToken, `/v1/expenses/${expenseId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'summary'] });
      navigate({ to: '/app/expenses' });
    },
  });

  // Create dispute mutation
  const createDisputeMutation = useMutation({
    mutationFn: (reason: string) =>
      apiJson(getToken, `/v1/expenses/${expenseId}/dispute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expense', expenseId] });
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
      setDisputeModalVisible(false);
      setDisputeReason('');
    },
  });

  // Resolve dispute mutation
  const resolveDisputeMutation = useMutation({
    mutationFn: (disputeId: number) =>
      apiJson(getToken, `/v1/expenses/${expenseId}/dispute/${disputeId}/resolve`, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expense', expenseId] });
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });

  const handleDelete = () => {
    if (window.confirm(`Delete "${expense?.name}"? This action cannot be undone.`)) {
      deleteMutation.mutate();
    }
  };

  const handleCreateDispute = () => {
    if (!disputeReason.trim()) return;
    createDisputeMutation.mutate(disputeReason.trim());
  };

  const handleResolveDispute = (disputeId: number) => {
    if (window.confirm('Are you sure you want to resolve this dispute?')) {
      resolveDisputeMutation.mutate(disputeId);
    }
  };

  const imageAttachments = (expense?.attachments ?? []).filter((a) =>
    a.attachment_type.startsWith('image/')
  );
  const pdfAttachments = (expense?.attachments ?? []).filter(
    (a) =>
      a.attachment_type === 'application/pdf' ||
      a.name.toLowerCase().endsWith('.pdf')
  );

  if (expenseQuery.isLoading) {
    return (
      <div className='min-h-screen bg-sky-light flex items-center justify-center'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  if (expenseQuery.isError || !expense) {
    return (
      <div className='min-h-screen bg-sky-light'>
        <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
          <div className='max-w-lg mx-auto px-6 py-4'>
            <Link
              to='/app/expenses'
              className='inline-flex items-center gap-2 text-primary hover:underline'
            >
              <FiArrowLeft /> Back
            </Link>
          </div>
        </header>
        <div className='max-w-lg mx-auto px-6 py-8 text-center'>
          <p className='text-gray-500'>Couldn't load this expense.</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-sky-light'>
      {/* Mobile Header - hidden on desktop */}
      <header className='lg:hidden bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg mx-auto px-6 py-4'>
          <Link
            to='/app/expenses'
            className='inline-flex items-center gap-2 text-primary hover:underline'
          >
            <FiArrowLeft /> Back
          </Link>
        </div>
      </header>

      {/* Desktop Header - shown only on large screens */}
      <header className='hidden lg:block bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-2xl mx-auto px-8 py-4'>
          <h1
            className='text-xl font-semibold text-gray-900'
            style={{
              fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            {expense.name}
          </h1>
        </div>
      </header>

      <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-6 space-y-4'>
        {/* Paid Back Banner */}
        {expense.is_fully_paid_back && (
          <div className='flex items-center gap-4 bg-primary rounded-2xl p-4'>
            <div className='w-11 h-11 rounded-full bg-white/20 flex items-center justify-center'>
              <FiCheckCircle className='w-6 h-6 text-white' />
            </div>
            <div className='flex-1'>
              <p className='text-lg font-bold text-white'>Paid Back</p>
              <p className='text-sm text-white/85'>This expense has been fully repaid</p>
            </div>
          </div>
        )}

        {/* Title with category chip */}
        <div className='flex items-center justify-between gap-3'>
          <h1
            className='lg:hidden text-2xl font-bold text-gray-900 flex-1'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            {expense.name}
          </h1>
          <div
            className='flex items-center gap-2 px-3 py-1.5 rounded-full border'
            style={{ backgroundColor: `${categoryColor}10`, borderColor: `${categoryColor}30` }}
          >
            <div
              className='w-5 h-5 rounded-full flex items-center justify-center'
              style={{ backgroundColor: `${categoryColor}20` }}
            >
              <IconComponent className='w-3 h-3 text-gray-600' />
            </div>
            <span className='text-sm font-semibold text-gray-700'>{categoryName}</span>
          </div>
        </div>

        {/* Disputes section */}
        {hasDispute && (
          <Card>
            <h3 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
              Disputes
            </h3>
            {(expense.disputes ?? []).map((d) => (
              <div
                key={d.id}
                className='border-b border-gray-100 last:border-0 pb-3 last:pb-0 mb-3 last:mb-0'
              >
                <span className='text-xs font-bold text-amber-500 uppercase'>{d.state}</span>
                <p className='text-gray-600 text-sm mt-1'>{d.reason}</p>
                <p className='text-xs text-gray-400 mt-1'>
                  Created by {getUserName(d.creator_user_id)}
                </p>
                <div className='flex gap-3 mt-3'>
                  <button className='flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/20 transition-colors'>
                    <FiMessageCircle className='w-4 h-4' />
                    Discuss
                  </button>
                  {currentUser &&
                    d.creator_user_id === currentUser.id &&
                    d.state !== 'RESOLVED' && (
                      <button
                        onClick={() => handleResolveDispute(d.id)}
                        disabled={resolveDisputeMutation.isPending}
                        className='flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-100 text-green-700 text-sm font-semibold hover:bg-green-200 transition-colors disabled:opacity-50'
                      >
                        <FiCheckCircle className='w-4 h-4' />
                        Resolve
                      </button>
                    )}
                </div>
              </div>
            ))}
          </Card>
        )}

        {/* Amount and date */}
        <Card>
          <div className='flex justify-between py-2'>
            <span className='text-gray-500'>Amount</span>
            <span className='font-bold text-gray-900'>{formatMoney(expense.amount_cents)}</span>
          </div>
          <div className='h-px bg-gray-100 my-1' />
          <div className='flex justify-between py-2'>
            <span className='text-gray-500'>Date</span>
            <span className='font-semibold text-gray-700'>
              {new Date(expense.purchase_date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>
          {expense.note && (
            <>
              <div className='h-px bg-gray-100 my-1' />
              <div className='flex justify-between py-2'>
                <span className='text-gray-500'>Note</span>
                <span className='font-semibold text-gray-700'>{expense.note}</span>
              </div>
            </>
          )}
        </Card>

        {/* Created by */}
        <Card>
          <h3 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-2'>
            Created by
          </h3>
          <p className='font-semibold text-gray-900'>{getUserName(expense.creator_user_id)}</p>
        </Card>

        {/* Purchased by */}
        {expense.purchaser_user_id && (
          <Card>
            <h3 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-2'>
              Purchased by
            </h3>
            <p className='font-semibold text-gray-900'>{getUserName(expense.purchaser_user_id)}</p>
          </Card>
        )}

        {/* Splits */}
        <Card>
          <h3 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-2'>Splits</h3>
          {(expense.splits ?? []).length === 0 ? (
            <p className='text-gray-500'>No splits recorded.</p>
          ) : (
            (expense.splits ?? []).map((s) => (
              <p key={s.id} className='font-semibold text-gray-900'>
                {getUserName(s.split_for_user_id)}: {formatMoney(s.amount_cents)} ({s.amount_percent}
                %)
              </p>
            ))
          )}
        </Card>

        {/* Beneficiaries */}
        <Card>
          <h3 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-2'>For</h3>
          {(expense.beneficiaries ?? []).length === 0 ? (
            <p className='text-gray-500'>No beneficiaries specified.</p>
          ) : (
            (expense.beneficiaries ?? []).map((b) => (
              <div key={b.beneficiary_id} className='border-b border-gray-100 last:border-0 py-2'>
                <p className='font-semibold text-gray-900'>{b.name ?? 'Unnamed'}</p>
                <p className='text-xs text-gray-500'>
                  {b.type === 'CHILD' ? 'Child' : b.type === 'PET' ? 'Pet' : 'Other'}
                </p>
              </div>
            ))
          )}
        </Card>

        {/* Attachments */}
        {(imageAttachments.length > 0 || pdfAttachments.length > 0) && (
          <Card>
            <h3 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
              Attachments
            </h3>
            <div className='flex flex-wrap gap-3'>
              {imageAttachments.map((attachment, index) => (
                <button
                  key={attachment.id}
                  onClick={() => {
                    setLightboxIndex(index);
                    setLightboxOpen(true);
                  }}
                  className='w-24 h-24 rounded-xl overflow-hidden border border-gray-200 hover:border-primary transition-colors'
                >
                  <img
                    src={attachment.download_url}
                    alt={attachment.name}
                    className='w-full h-full object-cover'
                  />
                </button>
              ))}
              {pdfAttachments.map((attachment) => (
                <a
                  key={attachment.id}
                  href={attachment.download_url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='w-24 h-24 rounded-xl border border-gray-200 hover:border-primary transition-colors flex flex-col items-center justify-center p-2 text-center'
                >
                  <span className='text-2xl'>📄</span>
                  <span className='text-[10px] text-gray-600 mt-1 line-clamp-2'>{attachment.name}</span>
                </a>
              ))}
            </div>
          </Card>
        )}

        {/* Linked Payments */}
        {(expense.linked_payments ?? []).length > 0 && (
          <Card>
            <h3 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
              Linked Payments
            </h3>
            {(expense.linked_payments ?? []).map((payment) => (
              <Link
                key={payment.id}
                to='/app/payments/$id'
                params={{ id: payment.id.toString() }}
                className='flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors'
              >
                <div>
                  <p className='font-bold text-gray-900'>{formatMoney(payment.amount_cents)}</p>
                  <p className='text-xs text-gray-500'>
                    {new Date(payment.payment_date).toLocaleDateString()}
                  </p>
                </div>
                <div className='text-right'>
                  <p className='text-sm text-gray-500'>
                    {payment.sender_user_id ? getUserName(payment.sender_user_id) : 'Unknown'} →{' '}
                    {payment.recipient_user_id ? getUserName(payment.recipient_user_id) : 'Unknown'}
                  </p>
                </div>
              </Link>
            ))}
          </Card>
        )}

        {/* Dispute button */}
        {!hasDispute && currentUser && expense.creator_user_id !== currentUser.id && (
          <button
            onClick={() => setDisputeModalVisible(true)}
            className='w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-amber-100 border border-amber-400 text-amber-800 font-bold hover:bg-amber-200 transition-colors'
          >
            <FaGavel className='w-4 h-4' />
            Dispute Expense
          </button>
        )}

        {/* Delete button */}
        {canDelete && (
          <Button
            variant='danger'
            fullWidth
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            loading={deleteMutation.isPending}
            className='bg-red-500 hover:bg-red-600 text-white'
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Expense'}
          </Button>
        )}
      </div>

      {/* Dispute Modal */}
      {disputeModalVisible && (
        <div className='fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50'>
          <div className='bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6'>
            <div className='flex justify-between items-center mb-4'>
              <h2 className='text-xl font-bold text-gray-900'>Dispute Expense</h2>
              <button
                onClick={() => {
                  setDisputeModalVisible(false);
                  setDisputeReason('');
                }}
                className='p-2 hover:bg-gray-100 rounded-full transition-colors'
              >
                <FiX className='w-5 h-5' />
              </button>
            </div>
            <p className='text-gray-500 mb-4'>
              Please provide a reason for disputing this expense.
            </p>
            <textarea
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
              placeholder='Enter reason for dispute...'
              rows={4}
              disabled={createDisputeMutation.isPending}
              className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary resize-none'
            />
            <div className='flex gap-3 mt-4'>
              <Button
                variant='secondary'
                fullWidth
                onClick={() => {
                  setDisputeModalVisible(false);
                  setDisputeReason('');
                }}
                disabled={createDisputeMutation.isPending}
              >
                Cancel
              </Button>
              <button
                onClick={handleCreateDispute}
                disabled={createDisputeMutation.isPending || !disputeReason.trim()}
                className='flex-1 py-3.5 rounded-2xl bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {createDisputeMutation.isPending ? 'Creating...' : 'Create Dispute'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && imageAttachments.length > 0 && (
        <div
          className='fixed inset-0 bg-black/95 flex items-center justify-center z-50'
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className='absolute top-6 right-6 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors'
          >
            <FiX className='w-6 h-6' />
          </button>
          <img
            src={imageAttachments[lightboxIndex].download_url}
            alt={imageAttachments[lightboxIndex].name}
            className='max-w-full max-h-full object-contain'
            onClick={(e) => e.stopPropagation()}
          />
          <div className='absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/50 text-white text-sm'>
            {lightboxIndex + 1} / {imageAttachments.length}
          </div>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/app/expenses/$id')({
  component: ExpenseDetailPage,
});
